import { useSignal, useSignalEffect } from "@preact/signals";
import type { ReadonlySignal } from "@preact/signals";
import type { FormStore } from "@formisch/preact";
import { setInput } from "@formisch/preact";

import { InputLabel } from "./input-label.js";
import { InputError } from "./input-error.js";
import { inputClass, type FieldProps } from "./shared.js";
import { formatPhoneInput } from "../../validation.js";
import { autocompleteFor, enterKeyHintFor } from "../../autofill.js";
import type { FormEmbedField } from "../../types.js";

interface PhoneInputProps {
  field: FormEmbedField;
  input: ReadonlySignal<any>;
  errors: ReadonlySignal<[string, ...string[]] | null>;
  props: FieldProps;
  disabled: boolean;
  form: FormStore;
  isLast?: boolean;
}

export function PhoneInput({ field, input, errors, props, disabled, form, isLast = false }: PhoneInputProps) {
  const signal = useSignal(input.value ?? "");
  useSignalEffect(() => { signal.value = input.value ?? ""; });

  const errorClass = errors.value ? "border-theme-destructive" : "border-theme-input";

  function handleInput(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    const formatted = formatPhoneInput(raw);
    signal.value = formatted;
    setInput(form, { path: [field.name] as any, input: formatted });
  }

  return (
    <div class={`flex flex-col gap-1 ${field.width === "half" ? "w-[calc(50%-6px)]" : "w-full"}`} data-fv-field={field.name}>
      <InputLabel label={field.label} required={field.required} description={field.description} />
      <input
        {...props}
        class={`${inputClass} ${errorClass}`}
        type="tel"
        autocomplete={autocompleteFor(field)}
        inputmode="tel"
        enterkeyhint={enterKeyHintFor(isLast)}
        placeholder={field.placeholder || "(555) 555-5555"}
        value={signal}
        disabled={disabled}
        onInput={handleInput}
        aria-invalid={!!errors.value}
        aria-errormessage={`${props.name}-error`}
      />
      <InputError name={props.name} errors={errors} />
    </div>
  );
}
